import { Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { TIMEZONE_OPTIONS } from "../../constants/constants";
import { useAccountTimezone } from "../../hooks/timezone";
import { dateInitialValues, dateValidationSchema } from "./validation";

export type VehicleTripFiltersData = {
  selected_date?: string;
  date_from: string;
  date_to: string;
  time_from: string;
  time_to: string;
  timezone: string;
  calculatedUnixTimestampFrom: number | null;
  calculatedUnixTimestampTo: number | null;
};

type ModalVehicleTripFiltersProps = {
  loadingData: boolean;
  savedFilterValues?: VehicleTripFiltersData; // filter values saved from last user change
  onFilterChange?: (data: VehicleTripFiltersData) => void;
};

const ModalVehicleTripFilters: FC<ModalVehicleTripFiltersProps> = ({
  loadingData,
  savedFilterValues,
  onFilterChange,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.vehicleTripFilter",
  });

  // retrieve account timezone
  const { tzForDisplay } = useAccountTimezone();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showVehicleFilter: false }));
  };

  if (modalsState.showVehicleFilter === false) return null;
  return (
    <FTModal
      visible={modalsState.showVehicleFilter}
      heading={t("heading")}
      onHide={hideModal}
      containerStyle="w-[90%] md:w-[480px] md:max-w-[calc(100vw-4rem)]"
    >
      <Formik
        initialValues={{
          ...(savedFilterValues ?? dateInitialValues),
          timezone: tzForDisplay,
        }}
        validationSchema={dateValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          onFilterChange && onFilterChange(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          setFieldValue,
          setFieldTouched,
          resetForm,
          submitForm,
        }) => {
          return (
            <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
              <AdminFormFieldInput
                customWrapperClass="col-span-12 md:col-span-3"
                customInputClass="text-sm"
                label={t("selected_date")}
                type="date-picker" // type="datetime-picker"
                id="selected_date"
                name="selected_date"
                value={values.selected_date || values.date_from} // value={`${values.date_from} ${values.time_from}`}
                maxTime={new Date()}
                onDatepickerChange={(field_name: string, value: any) => {
                  const valueArr = value.split("T");
                  setFieldValue("selected_date", valueArr[0]);
                  // setFieldValue("date_from", valueArr[0]);
                  // setFieldValue("time_from", valueArr[1]);
                }}
                onBlur={handleBlur}
                error={errors.date_from}
                touched={touched.date_from}
                size="lg"
              />

              {/* <AdminFormFieldInput
                customWrapperClass="col-span-3"
                customInputClass="text-xs"
                label={t("date_to")}
                type="datetime-picker"
                id="date_to"
                name="date_to"
                value={`${values.date_to} ${values.time_to}`}
                maxTime={new Date()}
                onDatepickerChange={(field_name: string, value: any) => {
                  const valueArr = value.split("T");
                  setFieldValue("date_to", valueArr[0]);
                  setFieldValue("time_to", valueArr[1]);
                }}
                onBlur={handleBlur}
                error={errors.date_to}
                touched={touched.date_to}
              /> */}

              <AdminFormFieldDropdown
                customWrapperClass="col-span-12 md:col-span-3"
                label={t("timezone")}
                id="timezone"
                name="timezone"
                value={values.timezone}
                options={[
                  {
                    value: "",
                    label: t("all_timezones"),
                  },
                  ...TIMEZONE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
                ]}
                onChange={(e) => setFieldValue("timezone", e?.value)}
                onBlur={(e) => {
                  setFieldTouched("timezone", true);
                  handleBlur(e);
                }}
                error={errors.timezone}
                touched={touched.timezone}
              />

              <AdminFormFieldSubmit
              customWrapperClass="col-span-12 md:col-span-6"
                label={tMain("confirm")}
                type="submit"
                variant="primary"
                // onClick={hideModal} // disabled to let the form submit
                disabled={loadingData}
              />

              <AdminFormFieldSubmit
              customWrapperClass="col-span-12 md:col-span-6"
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={hideModal}
                disabled={loadingData}
              />
            </form>
          );
        }}
      </Formik>
    </FTModal>
  );
};

export default ModalVehicleTripFilters;
